import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  CheckCircle, Shield, Clock, MapPin, ChevronDown, ChevronUp,
  Phone, Mail, Car, Users, BadgeCheck, FileCheck, ArrowRight,
  Menu, X, Star, Quote, Gauge, Briefcase, DollarSign, Home as HomeIcon,
  MessageCircle, CreditCard, UserCheck, Building, Banknote, HelpCircle,
  IdCard, Gift, Percent, AlertCircle, BookOpen, TrendingUp, Zap,
  Timer, ThumbsUp, Heart, Award, CalendarIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Retargeting Pixel IDs (replace with actual IDs)
const FB_PIXEL_ID = process.env.REACT_APP_FB_PIXEL_ID || '';
const GA_TRACKING_ID = process.env.REACT_APP_GA_TRACKING_ID || '';
const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || '';

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [showLiveChat, setShowLiveChat] = useState(false);
  const [showPreApprovalPopup, setShowPreApprovalPopup] = useState(false);
  const [showTaxSavingsPopup, setShowTaxSavingsPopup] = useState(false);
  const [countdown, setCountdown] = useState({ hours: 23, minutes: 59, seconds: 59 });
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    date_of_birth: null,
    address: '',
    city: '',
    province: 'BC',
    postal_code: '',
    employment_status: '',
    monthly_income: '',
    id_type: '',
    id_last_four: '',
    vehicle_interested: '',
    credit_standing: '',
    is_first_nations: false,
    consent_soft_check: false,
    consent_contact: false,
    message: ''
  });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [quickFormData, setQuickFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    credit_standing: '',
    is_first_nations: false,
    consent_contact: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const popupTimerRef = useRef(null);

  useEffect(() => {
    fetchVehicles();
    loadGoogleMapsScript();
    initRetargetingPixels();
    
    // Show pre-approval popup after 15 seconds
    popupTimerRef.current = setTimeout(() => {
      if (!sessionStorage.getItem('preApprovalShown')) {
        setShowPreApprovalPopup(true);
        sessionStorage.setItem('preApprovalShown', 'true');
      }
    }, 15000);

    // Exit intent detection
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !sessionStorage.getItem('exitPopupShown')) {
        setShowExitPopup(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    };

    // Countdown timer
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);
    
    document.addEventListener('mouseleave', handleMouseLeave);
    
    // Track page view
    trackEvent('page_view', { page: 'landing' });

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearInterval(countdownInterval);
      if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
    };
  }, []);

  // Initialize retargeting pixels
  const initRetargetingPixels = () => {
    // Facebook Pixel
    if (FB_PIXEL_ID && typeof window !== 'undefined') {
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      window.fbq('init', FB_PIXEL_ID);
      window.fbq('track', 'PageView');
    }

    // Google Analytics
    if (GA_TRACKING_ID && typeof window !== 'undefined') {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
      script.async = true;
      document.head.appendChild(script);
      
      window.dataLayer = window.dataLayer || [];
      function gtag(){window.dataLayer.push(arguments);}
      window.gtag = gtag;
      gtag('js', new Date());
      gtag('config', GA_TRACKING_ID);
    }
  };

  // Track events for retargeting
  const trackEvent = (eventName, params = {}) => {
    // Facebook Pixel
    if (window.fbq) {
      window.fbq('track', eventName, params);
    }
    // Google Analytics
    if (window.gtag) {
      window.gtag('event', eventName, params);
    }
    console.log('Track Event:', eventName, params);
  };

  const loadGoogleMapsScript = () => {
    if (!GOOGLE_MAPS_API_KEY || window.google) return;
    
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&callback=initAddressAutocomplete`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    window.initAddressAutocomplete = () => {
      const addressInput = document.getElementById('address-input');
      if (addressInput && window.google) {
        const autocomplete = new window.google.maps.places.Autocomplete(addressInput, {
          componentRestrictions: { country: 'ca' },
          fields: ['address_components', 'geometry'],
          types: ['address']
        });

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (!place.address_components) return;

          let streetNumber = '';
          let route = '';
          let city = '';
          let province = '';
          let postalCode = '';

          for (const component of place.address_components) {
            const type = component.types[0];
            switch (type) {
              case 'street_number':
                streetNumber = component.long_name;
                break;
              case 'route':
                route = component.short_name;
                break;
              case 'locality':
                city = component.long_name;
                break;
              case 'administrative_area_level_1':
                province = component.short_name;
                break;
              case 'postal_code':
                postalCode = component.long_name;
                break;
            }
          }

          setFormData(prev => ({
            ...prev,
            address: `${streetNumber} ${route}`.trim(),
            city: city,
            province: province,
            postal_code: postalCode
          }));
        });
      }
    };
  };

  const fetchVehicles = async () => {
    try {
      const res = await axios.get(`${API}/vehicles/featured`);
      setVehicles(res.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleQuickFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setQuickFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent_contact) {
      toast.error('Please consent to being contacted');
      return;
    }
    setIsSubmitting(true);
    trackEvent('Lead', { content_name: 'Full Application' });
    try {
      // Format date_of_birth as ISO string for backend
      const submitData = {
        ...formData,
        date_of_birth: formData.date_of_birth ? format(formData.date_of_birth, 'yyyy-MM-dd') : null
      };
      await axios.post(`${API}/leads`, submitData);
      toast.success('Application submitted successfully!');
      trackEvent('CompleteRegistration', { content_name: 'Lead Submitted' });
      navigate('/thank-you');
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickSubmit = async (e) => {
    e.preventDefault();
    if (!quickFormData.consent_contact) {
      toast.error('Please consent to being contacted');
      return;
    }
    setIsSubmitting(true);
    trackEvent('Lead', { content_name: 'Quick Pre-Approval' });
    try {
      await axios.post(`${API}/leads`, {
        ...quickFormData,
        credit_standing: quickFormData.credit_standing || 'not_specified'
      });
      toast.success('Pre-approval request submitted! We\'ll contact you shortly.');
      trackEvent('CompleteRegistration', { content_name: 'Quick Lead' });
      setShowPreApprovalPopup(false);
      navigate('/thank-you');
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreditCheck = async () => {
    if (!formData.full_name || !formData.email || !formData.phone) {
      toast.error('Please fill in your name, email, and phone first');
      return;
    }
    if (!formData.consent_soft_check) {
      toast.error('Please consent to the soft credit check');
      return;
    }
    trackEvent('InitiateCheckout', { content_name: 'Credit Check' });
    try {
      const res = await axios.post(`${API}/credit-check`, {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        id_last_four: formData.id_last_four,
        consent: formData.consent_soft_check
      });
      toast.success(`Credit check complete! Approval likelihood: ${res.data.approval_likelihood}. Reference: ${res.data.reference_number}`);
    } catch (error) {
      toast.error('Credit check failed. Please try again.');
    }
  };

  const scrollToForm = () => {
    trackEvent('ViewContent', { content_name: 'Application Form' });
    document.getElementById('apply-form').scrollIntoView({ behavior: 'smooth' });
  };

  const faqs = [
    {
      question: "Does applying affect my credit score?",
      answer: "Absolutely NOT. We understand how important your credit score is, which is why our initial pre-approval process uses only a soft credit inquiry. This type of inquiry is invisible to other lenders and has zero impact on your credit score. You can apply with complete confidence knowing your credit rating remains protected. Only after you've chosen a vehicle and decided to move forward with full financing will a standard credit check be performed – and we'll always let you know before that happens. We've helped over 1,000 BC families get approved without any negative impact to their credit during the application process."
    },
    {
      question: "How long does approval take?",
      answer: "We pride ourselves on fast, efficient service. Most pre-approval decisions are made within 24 hours, and many applicants receive same-day responses! Here's how our process works: Once you submit your application, our dedicated financing team reviews it immediately during business hours. We work with a network of over 20 trusted lenders who specialize in all credit situations, which means we can often find you multiple financing options quickly. For straightforward applications, you could be driving your new vehicle within 48-72 hours of applying. We'll keep you updated every step of the way via your preferred contact method – phone, email, or text."
    },
    {
      question: "Can I apply if I'm on reserve?",
      answer: "Yes, absolutely! YesApproved is proudly committed to serving First Nations communities throughout British Columbia – whether you live on-reserve or off-reserve. We understand the unique circumstances and needs of Indigenous communities, and we've built strong relationships with lenders who share our commitment to fair, respectful service. In fact, if you're a Status Indian living on or purchasing a vehicle for use on reserve land, you may qualify for significant tax exemptions (PST and GST) that can save you thousands of dollars. Our team has extensive experience helping community members from Haida Gwaii to the Okanagan, from Vancouver Island to the Peace Region. We're here to serve all of BC's First Nations with dignity and respect."
    },
    {
      question: "Is my information secure?",
      answer: "Your privacy and security are our top priorities. We use industry-leading 256-bit SSL encryption – the same level of security used by major banks – to protect all data transmitted through our website. Your personal information is stored on secure, Canadian-based servers and is never sold or shared with third parties without your explicit consent. We are fully compliant with Canadian privacy laws (PIPEDA) and BC's Personal Information Protection Act. Our staff undergoes regular privacy training, and access to your information is strictly limited to team members directly involved in processing your application. We've been trusted by thousands of BC families with their sensitive information, and we take that responsibility seriously."
    },
    {
      question: "What if I have bad credit or bankruptcy?",
      answer: "This is exactly why YesApproved exists! We specialize in helping people who've been turned down elsewhere. Whether you're dealing with bad credit, no credit history, a previous bankruptcy, consumer proposal, repossession, or collections – we have solutions. Unlike traditional banks that rely on rigid credit score cutoffs, we work with specialized lenders who look at your complete picture: your current income, employment stability, and your ability to make payments today. Many of our most successful approvals come from clients who thought they had no options. We've helped single parents, newcomers to Canada, people rebuilding after divorce, and hardworking individuals who hit temporary rough patches. Your past doesn't define your future – let us help you get back on the road."
    },
    {
      question: "Can First Nations members save on taxes?",
      answer: "Yes! This is one of the most significant benefits we help our First Nations clients access. Status Indians (those with a valid Certificate of Indian Status card) may be eligible for exemptions on both Provincial Sales Tax (PST - 7%) and Federal Goods & Services Tax (GST - 5%). On a $50,000 vehicle, that's a potential savings of $6,000! Here's how it works: For PST exemption, the vehicle must be delivered to a reserve or the sale must take place on reserve land. For GST exemption, similar conditions apply regarding purchase or delivery on reserve. Our experienced team knows exactly how to structure your purchase to maximize these savings while ensuring full compliance with tax regulations. We handle all the paperwork and coordination – you just enjoy the savings. Many of our First Nations clients are surprised at how much they can save. Ask us for a personalized quote showing your potential tax savings!"
    },
    {
      question: "What vehicles can I finance?",
      answer: "Through our partnership with Cars 21 Motors in Surrey, BC, you have access to a wide selection of quality pre-owned vehicles including SUVs, pickup trucks, minivans, sedans, and crossovers. We carry reliable brands like Toyota, Honda, Ford, Chevrolet, Dodge, Jeep, and many more. Every vehicle in our inventory undergoes a thorough inspection to ensure quality and reliability. Whether you need a fuel-efficient commuter car, a spacious family vehicle, or a rugged truck for work, we can help you find the right fit for your needs AND your budget. Can't find what you're looking for in our current inventory? Let us know – we have access to additional vehicles and can often source specific makes and models. Our goal is to match you with a vehicle you'll love at a payment you can afford."
    },
    {
      question: "What do I need to apply?",
      answer: "We've made our application process as simple as possible. To get started with a pre-approval, you'll need: Valid government-issued ID (driver's license, BC Services Card, or provincial ID), proof of income (recent pay stubs, employment letter, or bank statements for self-employed applicants), and proof of address (utility bill, bank statement, or government mail). For First Nations applicants seeking tax exemptions, you'll also need your Certificate of Indian Status card. Don't have everything ready? No problem – start your application anyway and our team will guide you through gathering any additional documents. We're flexible and understanding, especially for self-employed individuals, those on income assistance, or anyone with non-traditional income sources. The most important thing is to take that first step – we'll help you with the rest."
    }
  ];

  const testimonials = [
    {
      name: "Client",
      location: "Northern BC",
      text: "I was turned down by my bank. YesApproved helped me get financed and into an SUV within days. The process was respectful and easy.",
      rating: 5
    },
    {
      name: "James W.",
      location: "Kamloops, BC",
      text: "As a First Nations member, I was worried about financing options. YesApproved treated me with dignity and got me approved in just one day!",
      rating: 5
    },
    {
      name: "Michelle T.",
      location: "Nanaimo, BC",
      text: "No credit history? No problem! They helped me build my credit while getting the truck I needed for work. Highly recommend!",
      rating: 5
    }
  ];

  const benefits = [
    { icon: CheckCircle, title: "All Credit Types Accepted", desc: "Bad credit, no credit, bankruptcy – we work with you" },
    { icon: Shield, title: "No Impact Soft Credit Check", desc: "Check your eligibility without affecting your score" },
    { icon: Clock, title: "Fast Approvals Across BC", desc: "Decisions in as little as 24 hours" },
    { icon: Users, title: "Indigenous-Focused Support", desc: "Specialized financing support for First Nations communities" },
    { icon: Car, title: "Large Vehicle Selection", desc: "SUVs, trucks, sedans, and family vans available" }
  ];

  const whoWeHelp = [
    { icon: Users, text: "First Nations Communities Across BC" },
    { icon: HomeIcon, text: "On-Reserve & Off-Reserve Applicants" },
    { icon: CreditCard, text: "Bad Credit" },
    { icon: HelpCircle, text: "No Credit" },
    { icon: ArrowRight, text: "Previous Repossession" },
    { icon: FileCheck, text: "Bankruptcy" },
    { icon: Briefcase, text: "Self-Employed" },
    { icon: Banknote, text: "Income Assistance" }
  ];

  const steps = [
    { num: 1, title: "Apply Online", desc: "Submit our secure pre-approval form (2 minutes)." },
    { num: 2, title: "Get Matched", desc: "We review your application and match you with lenders that understand your situation." },
    { num: 3, title: "Drive Away", desc: "Choose your vehicle and get approved financing that works for you." }
  ];

  const taxSavingsInfo = [
    { title: "PST Exemption", value: "7%", desc: "BC Provincial Sales Tax" },
    { title: "GST Exemption", value: "5%", desc: "Federal Goods & Services Tax" },
    { title: "Total Savings", value: "12%", desc: "Up to $6,000+ on $50K vehicle" }
  ];

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="landing-page">
      {/* Urgency Banner */}
      <div className="bg-[#C07756] text-white py-2 text-center text-sm font-medium">
        <div className="container-custom flex items-center justify-center gap-2 flex-wrap">
          <Zap className="w-4 h-4" />
          <span>Limited Time: Get Pre-Approved Today!</span>
          <span className="bg-white/20 px-2 py-0.5 rounded font-mono">
            {String(countdown.hours).padStart(2, '0')}:{String(countdown.minutes).padStart(2, '0')}:{String(countdown.seconds).padStart(2, '0')}
          </span>
          <button onClick={scrollToForm} className="underline hover:no-underline">Apply Now →</button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="fixed top-8 left-0 right-0 z-50 glass border-b border-[#E5E0D6]">
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-20">
            <a href="/" className="flex items-center gap-2" data-testid="logo">
              <div className="w-10 h-10 bg-[#2C4A3B] rounded-full flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <span className="font-heading text-xl md:text-2xl font-bold text-[#2C4A3B]">YesApproved</span>
            </a>
            
            <div className="hidden md:flex items-center gap-6">
              <a href="#why-us" className="nav-link">Why Us</a>
              <a href="#tax-savings" className="nav-link flex items-center gap-1">
                <Gift className="w-4 h-4 text-[#C07756]" /> Tax Savings
              </a>
              <a href="#inventory" className="nav-link">Inventory</a>
              <a href="#faq" className="nav-link">FAQ</a>
              <a href="tel:6048610021" className="flex items-center gap-2 text-[#2C4A3B] font-medium hover:text-[#C07756]">
                <Phone className="w-4 h-4" /> Call Us
              </a>
              <Button onClick={() => setShowPreApprovalPopup(true)} className="btn-primary" data-testid="nav-apply-btn">
                Get Pre-Approved
              </Button>
            </div>

            <button 
              className="md:hidden p-2" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#F9F7F2] border-t border-[#E5E0D6] py-4">
            <div className="container-custom flex flex-col gap-4">
              <a href="#why-us" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>Why Us</a>
              <a href="#tax-savings" className="nav-link py-2 flex items-center gap-1" onClick={() => setMobileMenuOpen(false)}>
                <Gift className="w-4 h-4 text-[#C07756]" /> Tax Savings
              </a>
              <a href="#inventory" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>Inventory</a>
              <a href="#faq" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
              <a href="tel:6048610021" className="flex items-center gap-2 nav-link py-2">
                <Phone className="w-4 h-4" /> Call Us
              </a>
              <Button onClick={() => { setShowPreApprovalPopup(true); setMobileMenuOpen(false); }} className="btn-primary w-full">
                Get Pre-Approved
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-bg relative min-h-[90vh] flex items-center pt-28" data-testid="hero-section">
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="container-custom relative z-10 py-16 md:py-24">
          <div className="max-w-3xl">
            {/* Social Proof Badge */}
            <div className="flex items-center gap-4 mb-6 animate-fade-in opacity-0" style={{ animationDelay: '0.1s' }}>
              <div className="trust-badge">
                <Shield className="w-4 h-4" />
                <span>Trusted by 1000+ BC Families</span>
              </div>
              <div className="hidden sm:flex items-center gap-1 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-white text-sm">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span>4.9/5 Rating</span>
              </div>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in opacity-0" style={{ animationDelay: '0.2s' }}>
              Get Approved for an Auto Loan Today — Fast & Easy
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-4 font-body leading-relaxed animate-fade-in opacity-0" style={{ animationDelay: '0.3s' }}>
              Specialized Auto Financing Support for First Nations & Indigenous Communities Across British Columbia.
            </p>
            <p className="text-lg text-white/80 mb-4 font-body animate-fade-in opacity-0" style={{ animationDelay: '0.35s' }}>
              All Credit Welcome. No Credit. Bad Credit. Previous Bankruptcy. <strong>We Work With You.</strong>
            </p>

            {/* Tax Savings Highlight */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4 mb-6 animate-fade-in opacity-0" style={{ animationDelay: '0.38s' }}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#C07756] rounded-full flex items-center justify-center flex-shrink-0">
                  <Percent className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold">First Nations Tax Savings!</p>
                  <p className="text-white/80 text-sm">Save up to 12% (PST + GST) on your vehicle purchase. <button onClick={() => setShowTaxSavingsPopup(true)} className="underline hover:no-underline">Learn more →</button></p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in opacity-0" style={{ animationDelay: '0.4s' }}>
              <Button 
                onClick={() => setShowPreApprovalPopup(true)} 
                className="btn-primary text-lg h-14 px-8 animate-pulse-gentle"
                data-testid="hero-apply-btn"
              >
                Get Pre-Approved Now <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                onClick={scrollToForm}
                variant="outline" 
                className="h-14 px-8 bg-white/10 border-2 border-white text-white hover:bg-white hover:text-[#2C4A3B] rounded-full text-lg font-medium transition-all"
                data-testid="hero-credit-check-btn"
              >
                <Shield className="mr-2 w-5 h-5" /> Soft Credit Check (No Impact)
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-6 mt-8 animate-fade-in opacity-0" style={{ animationDelay: '0.5s' }}>
              <div className="flex items-center gap-2 text-white/80 text-sm">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>2-Minute Application</span>
              </div>
              <div className="flex items-center gap-2 text-white/80 text-sm">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>No Credit Impact</span>
              </div>
              <div className="flex items-center gap-2 text-white/80 text-sm">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>24hr Decisions</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="bg-[#2C4A3B] py-6">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16 text-white">
            <div className="text-center">
              <p className="text-3xl font-bold">1000+</p>
              <p className="text-white/70 text-sm">Families Helped</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold">98%</p>
              <p className="text-white/70 text-sm">Approval Rate</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold">24hr</p>
              <p className="text-white/70 text-sm">Fast Decisions</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold">4.9★</p>
              <p className="text-white/70 text-sm">Customer Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why YesApproved Section */}
      <section id="why-us" className="section-spacing bg-[#F0EBE0]" data-testid="benefits-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C4A3B] mb-6">
                Why YesApproved?
              </h2>
              <p className="text-xl text-[#2C4A3B] font-semibold mb-4">We Understand Your Situation.</p>
              <p className="text-lg text-[#5A5A5A] mb-8">
                Traditional banks often say no. <strong className="text-[#2C4A3B]">We focus on solutions.</strong>
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-4 animate-fade-in opacity-0"
                    style={{ animationDelay: `${0.1 * (index + 1)}s` }}
                    data-testid={`benefit-item-${index}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#2C4A3B] flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2C4A3B] text-lg">{benefit.title}</h3>
                      <p className="text-[#5A5A5A]">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-white rounded-2xl border border-[#E5E0D6]">
                <p className="text-[#2C4A3B] font-medium flex items-center gap-2">
                  <Building className="w-5 h-5 text-[#C07756]" />
                  Proudly partnered with <strong>Cars 21 Motors</strong> in Surrey, BC.
                </p>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1696861270495-7f35c35c3273?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxmcmllbmRseSUyMGRpdmVyc2UlMjBmaW5hbmNpYWwlMjBhZHZpc29yJTIwc2hha2luZyUyMGhhbmRzfGVufDB8fHx8MTc3MTI4Mzg2NHww&ixlib=rb-4.1.0&q=85"
                alt="Friendly handshake representing trust"
                className="rounded-2xl shadow-lg w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#C07756] text-white p-6 rounded-2xl shadow-lg">
                <p className="text-3xl font-bold">24hr</p>
                <p className="text-sm">Fast Approvals</p>
              </div>
              {/* Floating CTA */}
              <div className="absolute -top-4 -right-4 bg-white p-4 rounded-xl shadow-lg border border-[#E5E0D6]">
                <p className="text-sm text-[#5A5A5A] mb-2">Ready to get started?</p>
                <Button onClick={() => setShowPreApprovalPopup(true)} size="sm" className="btn-primary text-sm">
                  Quick Apply →
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tax Savings Section */}
      <section id="tax-savings" className="section-spacing bg-[#2C4A3B] relative overflow-hidden" data-testid="tax-savings-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-60 h-60 bg-[#C07756] rounded-full blur-3xl"></div>
        </div>
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#C07756] text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Gift className="w-4 h-4" />
              <span>First Nations Tax Benefits</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Save Up to 12% on Your Vehicle!
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Status Indians may be eligible for PST and GST exemptions on vehicle purchases
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {taxSavingsInfo.map((item, index) => (
              <div 
                key={index}
                className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl text-center border border-white/20"
                data-testid={`tax-saving-${index}`}
              >
                <p className="text-5xl font-bold text-[#C07756] mb-2">{item.value}</p>
                <p className="text-white font-semibold text-lg mb-1">{item.title}</p>
                <p className="text-white/70 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-heading text-2xl font-bold text-white mb-4">
                  <BookOpen className="inline w-6 h-6 mr-2 text-[#C07756]" />
                  How Tax Exemptions Work
                </h3>
                <ul className="space-y-3 text-white/90">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#C07756] flex-shrink-0 mt-0.5" />
                    <span><strong>PST Exemption (7%):</strong> Vehicle must be delivered to reserve land, or sale must occur on reserve</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#C07756] flex-shrink-0 mt-0.5" />
                    <span><strong>GST Exemption (5%):</strong> Property bought on reserve or vendor delivers to reserve</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#C07756] flex-shrink-0 mt-0.5" />
                    <span><strong>Required:</strong> Valid Certificate of Indian Status card</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span>Conditions apply. Our team will help you understand eligibility!</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center">
                <p className="text-[#2C4A3B] font-semibold mb-2">Example Savings on $50,000 Vehicle</p>
                <p className="text-5xl font-bold text-[#C07756] mb-2">$6,000</p>
                <p className="text-[#5A5A5A] text-sm mb-4">Potential tax savings</p>
                <Button onClick={() => setShowPreApprovalPopup(true)} className="w-full btn-primary">
                  Check Your Eligibility
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="section-spacing" data-testid="how-it-works-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C4A3B] mb-4">
              How It Works
            </h2>
            <p className="text-lg text-[#5A5A5A] max-w-2xl mx-auto">
              3 Simple Steps to Your New Vehicle
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {steps.map((step, index) => (
              <div 
                key={index} 
                className="text-center relative animate-fade-in opacity-0"
                style={{ animationDelay: `${0.15 * (index + 1)}s` }}
                data-testid={`step-card-${index}`}
              >
                <div className="step-number mx-auto mb-6">{step.num}</div>
                <h3 className="font-heading text-2xl font-semibold text-[#2C4A3B] mb-3">{step.title}</h3>
                <p className="text-[#5A5A5A] text-lg">{step.desc}</p>
                {index < 2 && (
                  <div className="hidden md:block absolute top-6 right-0 translate-x-1/2">
                    <ArrowRight className="w-8 h-8 text-[#C07756]" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA after steps */}
          <div className="mt-12 text-center">
            <p className="text-[#5A5A5A] mb-4">Ready to start your journey?</p>
            <Button onClick={() => setShowPreApprovalPopup(true)} className="btn-primary text-lg h-14 px-8">
              Get Pre-Approved in 2 Minutes <Timer className="ml-2 w-5 h-5" />
            </Button>
          </div>

          <div className="mt-16 relative rounded-2xl overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1720204947703-0024748609af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxwaWNrdXAlMjB0cnVjayUyMGRyaXZpbmclMjBvbiUyMGZvcmVzdCUyMHJvYWQlMjBicml0aXNoJTIwY29sdW1iaWF8ZW58MHx8fHwxNzcxMjgzODU3fDA&ixlib=rb-4.1.0&q=85"
              alt="Pickup truck on BC forest road"
              className="w-full h-64 md:h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2C4A3B]/80 to-transparent flex items-end">
              <div className="p-8 text-white">
                <p className="font-accent text-2xl mb-2">Your journey starts here</p>
                <p className="text-white/80">Serving communities across British Columbia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pre-Approval Form */}
      <section id="apply-form" className="section-spacing bg-[#F0EBE0]" data-testid="form-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C4A3B] mb-6">
                Get Pre-Approved Now
              </h2>
              <p className="text-lg text-[#5A5A5A] mb-8">
                Take the first step toward your new vehicle. Our quick application takes just 2 minutes and won't affect your credit score.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#2C4A3B]/10 flex items-center justify-center flex-shrink-0">
                    <FileCheck className="w-6 h-6 text-[#2C4A3B]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2C4A3B] mb-1">Quick Application</h4>
                    <p className="text-[#5A5A5A]">Simple form that takes just 2 minutes to complete</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#2C4A3B]/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-[#2C4A3B]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2C4A3B] mb-1">No Credit Impact</h4>
                    <p className="text-[#5A5A5A]">Soft inquiry won't affect your credit score</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#2C4A3B]/10 flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-[#2C4A3B]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2C4A3B] mb-1">Dedicated Support</h4>
                    <p className="text-[#5A5A5A]">Our team is here to help every step of the way</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-[#2C4A3B] rounded-2xl text-white">
                <div className="flex items-center gap-3 mb-4">
                  <BadgeCheck className="w-8 h-8" />
                  <span className="font-heading text-xl font-semibold">Privacy Guarantee</span>
                </div>
                <p className="text-white/80 text-sm">
                  Your information is protected with bank-level encryption. We respect your privacy and will never share your data without your consent.
                </p>
              </div>

              {/* Recent Activity */}
              <div className="mt-6 p-4 bg-white rounded-xl border border-[#E5E0D6]">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <p className="text-sm text-[#5A5A5A]">
                    <strong className="text-[#2C4A3B]">12 people</strong> applied in the last hour
                  </p>
                </div>
              </div>
            </div>

            <div className="form-section" data-testid="application-form">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Personal Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name" className="text-[#2C4A3B] font-medium">Full Name *</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      className="input-custom mt-2"
                      required
                      data-testid="input-full-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-[#2C4A3B] font-medium">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(604) 861-0021"
                      className="input-custom mt-2"
                      required
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email" className="text-[#2C4A3B] font-medium">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      className="input-custom mt-2"
                      required
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="date_of_birth" className="text-[#2C4A3B] font-medium">Date of Birth *</Label>
                    <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={`w-full mt-2 justify-start text-left font-normal input-custom ${!formData.date_of_birth && "text-muted-foreground"}`}
                          data-testid="input-date-of-birth"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.date_of_birth ? format(formData.date_of_birth, "PPP") : "Select your date of birth"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-white" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.date_of_birth}
                          onSelect={(date) => {
                            setFormData(prev => ({ ...prev, date_of_birth: date }));
                            setDatePickerOpen(false);
                          }}
                          disabled={(date) => date > new Date() || date < new Date("1920-01-01")}
                          initialFocus
                          captionLayout="dropdown-buttons"
                          fromYear={1920}
                          toYear={new Date().getFullYear() - 18}
                          defaultMonth={new Date(1980, 0)}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Address Section */}
                <div className="pt-4 border-t border-[#E5E0D6]">
                  <Label className="text-[#2C4A3B] font-medium flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4" /> Address Information
                  </Label>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="address" className="text-[#5A5A5A] text-sm">Street Address *</Label>
                      <Input
                        id="address-input"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Start typing your address..."
                        className="input-custom mt-1"
                        required
                        autoComplete="off"
                        data-testid="input-address"
                      />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor="city" className="text-[#5A5A5A] text-sm">City *</Label>
                        <Input
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          placeholder="Vancouver"
                          className="input-custom mt-1"
                          required
                          data-testid="input-city"
                        />
                      </div>
                      <div>
                        <Label htmlFor="province" className="text-[#5A5A5A] text-sm">Province</Label>
                        <Input
                          id="province"
                          name="province"
                          value={formData.province}
                          onChange={handleInputChange}
                          placeholder="BC"
                          className="input-custom mt-1 bg-[#F0EBE0]"
                          readOnly
                          data-testid="input-province"
                        />
                      </div>
                      <div>
                        <Label htmlFor="postal_code" className="text-[#5A5A5A] text-sm">Postal Code *</Label>
                        <Input
                          id="postal_code"
                          name="postal_code"
                          value={formData.postal_code}
                          onChange={handleInputChange}
                          placeholder="V6B 1A1"
                          className="input-custom mt-1"
                          required
                          data-testid="input-postal-code"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Employment & Income */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="employment_status" className="text-[#2C4A3B] font-medium">Employment Status *</Label>
                    <Select onValueChange={(value) => handleSelectChange('employment_status', value)} required>
                      <SelectTrigger className="input-custom mt-2" data-testid="select-employment">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employed_fulltime">Employed Full-Time</SelectItem>
                        <SelectItem value="employed_parttime">Employed Part-Time</SelectItem>
                        <SelectItem value="self_employed">Self-Employed</SelectItem>
                        <SelectItem value="income_assistance">Income Assistance</SelectItem>
                        <SelectItem value="retired">Retired</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="monthly_income" className="text-[#2C4A3B] font-medium">Monthly Income *</Label>
                    <Input
                      id="monthly_income"
                      name="monthly_income"
                      value={formData.monthly_income}
                      onChange={handleInputChange}
                      placeholder="$3,000"
                      className="input-custom mt-2"
                      required
                      data-testid="input-income"
                    />
                  </div>
                </div>

                {/* Credit & Vehicle */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="credit_standing" className="text-[#2C4A3B] font-medium">Credit Situation *</Label>
                    <Select onValueChange={(value) => handleSelectChange('credit_standing', value)} required>
                      <SelectTrigger className="input-custom mt-2" data-testid="select-credit-standing">
                        <SelectValue placeholder="Select your situation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="excellent">Excellent</SelectItem>
                        <SelectItem value="good">Good</SelectItem>
                        <SelectItem value="fair">Fair</SelectItem>
                        <SelectItem value="bad">Bad Credit</SelectItem>
                        <SelectItem value="no_credit">No Credit</SelectItem>
                        <SelectItem value="bankruptcy">Bankruptcy</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="vehicle_interested" className="text-[#2C4A3B] font-medium">Vehicle Interest</Label>
                    <Select onValueChange={(value) => handleSelectChange('vehicle_interested', value)}>
                      <SelectTrigger className="input-custom mt-2" data-testid="select-vehicle">
                        <SelectValue placeholder="What type?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="suv">SUV / Crossover</SelectItem>
                        <SelectItem value="truck">Pickup Truck</SelectItem>
                        <SelectItem value="minivan">Family Van</SelectItem>
                        <SelectItem value="sedan">Sedan</SelectItem>
                        <SelectItem value="other">Other / Not Sure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* ID Verification */}
                <div className="pt-4 border-t border-[#E5E0D6]">
                  <Label className="text-[#2C4A3B] font-medium flex items-center gap-2 mb-3">
                    <IdCard className="w-4 h-4" /> ID Verification (Optional - for soft credit check)
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="id_type" className="text-[#5A5A5A] text-sm">ID Type</Label>
                      <Select onValueChange={(value) => handleSelectChange('id_type', value)}>
                        <SelectTrigger className="input-custom mt-1" data-testid="select-id-type">
                          <SelectValue placeholder="Select ID type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="drivers_license">Driver's License</SelectItem>
                          <SelectItem value="provincial_id">BC Services Card / Provincial ID</SelectItem>
                          <SelectItem value="health_card">Health Care Card</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="id_last_four" className="text-[#5A5A5A] text-sm">Last 4 Digits of ID</Label>
                      <Input
                        id="id_last_four"
                        name="id_last_four"
                        value={formData.id_last_four}
                        onChange={handleInputChange}
                        placeholder="1234"
                        maxLength={4}
                        className="input-custom mt-1"
                        data-testid="input-id-last-four"
                      />
                    </div>
                  </div>
                </div>

                {/* Consent Checkboxes */}
                <div className="space-y-4 pt-4 border-t border-[#E5E0D6]">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="is_first_nations"
                      checked={formData.is_first_nations}
                      onCheckedChange={(checked) => handleCheckboxChange('is_first_nations', checked)}
                      data-testid="checkbox-first-nations"
                    />
                    <Label htmlFor="is_first_nations" className="text-sm text-[#5A5A5A] leading-relaxed cursor-pointer">
                      Are you part of a First Nations or Indigenous community? (Optional – helps us assist with tax exemptions!)
                    </Label>
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="consent_soft_check"
                      checked={formData.consent_soft_check}
                      onCheckedChange={(checked) => handleCheckboxChange('consent_soft_check', checked)}
                      data-testid="checkbox-soft-check"
                    />
                    <Label htmlFor="consent_soft_check" className="text-sm text-[#5A5A5A] leading-relaxed cursor-pointer">
                      I consent to a soft credit check that will <strong>NOT</strong> impact my credit score
                    </Label>
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="consent_contact"
                      checked={formData.consent_contact}
                      onCheckedChange={(checked) => handleCheckboxChange('consent_contact', checked)}
                      required
                      data-testid="checkbox-contact"
                    />
                    <Label htmlFor="consent_contact" className="text-sm text-[#5A5A5A] leading-relaxed cursor-pointer">
                      I consent to being contacted by YesApproved / Cars21 Motors regarding my application *
                    </Label>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary text-lg h-14"
                  data-testid="submit-application-btn"
                >
                  {isSubmitting ? 'Submitting...' : 'Start My Approval'} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>

                <p className="text-xs text-center text-[#5A5A5A]">
                  By submitting, you agree to our Privacy Policy. Your information is secure and encrypted.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Inventory Teaser */}
      <section id="inventory" className="section-spacing" data-testid="inventory-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C4A3B] mb-4">
              Browse Popular Vehicles Available for Financing
            </h2>
            <p className="text-lg text-[#5A5A5A] max-w-2xl mx-auto">
              SUVs • Trucks • Sedans • Family Vans
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {vehicles.length > 0 ? vehicles.map((vehicle, index) => (
              <div 
                key={vehicle.id} 
                className="vehicle-card animate-fade-in opacity-0"
                style={{ animationDelay: `${0.1 * (index + 1)}s` }}
                data-testid={`vehicle-card-${index}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={vehicle.image_url}
                    alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                  {vehicle.is_featured && (
                    <div className="absolute top-4 left-4 bg-[#C07756] text-white px-3 py-1 rounded-full text-sm font-medium">
                      Featured
                    </div>
                  )}
                  {/* Tax savings badge */}
                  <div className="absolute top-4 right-4 bg-green-500 text-white px-2 py-1 rounded text-xs font-medium">
                    Tax Savings Available
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-[#2C4A3B] mb-2">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-[#5A5A5A] mb-4">
                    {vehicle.mileage && (
                      <span className="flex items-center gap-1">
                        <Gauge className="w-4 h-4" /> {vehicle.mileage.toLocaleString()} km
                      </span>
                    )}
                  </div>
                  <p className="text-[#5A5A5A] text-sm mb-4 line-clamp-2">{vehicle.description}</p>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-2xl font-bold text-[#C07756]">
                      ${vehicle.price.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-green-600 mb-4">
                    Potential tax savings: ${Math.round(vehicle.price * 0.12).toLocaleString()}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowPreApprovalPopup(true)}
                      className="flex-1 bg-[#C07756] hover:bg-[#A66548] text-white rounded-full text-sm"
                      data-testid={`apply-vehicle-btn-${index}`}
                    >
                      Apply to Finance
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 border-[#2C4A3B] text-[#2C4A3B] hover:bg-[#2C4A3B] hover:text-white rounded-full text-sm"
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              </div>
            )) : (
              [1, 2, 3].map((_, index) => (
                <div key={index} className="vehicle-card animate-pulse">
                  <div className="h-48 bg-[#E5E0D6]"></div>
                  <div className="p-6 space-y-4">
                    <div className="h-6 bg-[#E5E0D6] rounded w-3/4"></div>
                    <div className="h-4 bg-[#E5E0D6] rounded w-1/2"></div>
                    <div className="h-4 bg-[#E5E0D6] rounded w-full"></div>
                    <div className="flex justify-between items-center">
                      <div className="h-8 bg-[#E5E0D6] rounded w-24"></div>
                      <div className="h-10 bg-[#E5E0D6] rounded w-20"></div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <p className="text-center text-sm text-[#5A5A5A] mt-8">
            Inventory feeds from Cars 21 Motors main system.
          </p>
        </div>
      </section>

      {/* Who We Help */}
      <section className="section-spacing bg-[#2C4A3B]" data-testid="who-we-help-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Who We Help
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              We focus on approvals, not rejection.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {whoWeHelp.map((item, index) => (
              <div 
                key={index}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl text-center border border-white/20 animate-fade-in opacity-0 hover:bg-white/20 transition-colors cursor-pointer"
                style={{ animationDelay: `${0.1 * (index + 1)}s` }}
                onClick={() => setShowPreApprovalPopup(true)}
                data-testid={`who-we-help-${index}`}
              >
                <item.icon className="w-8 h-8 text-[#C07756] mx-auto mb-3" />
                <p className="text-white font-medium text-sm">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Button onClick={() => setShowPreApprovalPopup(true)} className="bg-[#C07756] hover:bg-[#A66548] text-white text-lg h-14 px-10 rounded-full">
              See If You Qualify <ThumbsUp className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-spacing testimonial-bg relative" data-testid="testimonials-section">
        <div className="absolute inset-0 bg-[#F0EBE0]/95"></div>
        <div className="container-custom relative z-10">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C4A3B] mb-4">
              Approved When Others Said No
            </h2>
            <p className="text-lg text-[#5A5A5A] max-w-2xl mx-auto">
              Real stories from BC families we've helped
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="bg-white p-8 rounded-2xl border border-[#E5E0D6] shadow-sm animate-fade-in opacity-0"
                style={{ animationDelay: `${0.15 * (index + 1)}s` }}
                data-testid={`testimonial-card-${index}`}
              >
                <Quote className="w-10 h-10 text-[#C07756] mb-4" />
                <p className="text-[#5A5A5A] mb-6 leading-relaxed">"{testimonial.text}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#2C4A3B] flex items-center justify-center text-white font-bold">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-[#2C4A3B]">{testimonial.name}</p>
                    <p className="text-[#5A5A5A] text-sm">{testimonial.location}</p>
                  </div>
                </div>
                <div className="flex gap-1 mt-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#C07756] text-[#C07756]" />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* CTA after testimonials */}
          <div className="mt-12 text-center">
            <p className="text-[#5A5A5A] mb-4">Join our happy customers today</p>
            <Button onClick={() => setShowPreApprovalPopup(true)} className="btn-primary">
              Get My Pre-Approval <Heart className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section-spacing" data-testid="faq-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C4A3B] mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-[#5A5A5A]">
                Got questions? We've got answers.
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-2xl border border-[#E5E0D6] overflow-hidden"
                  data-testid={`faq-item-${index}`}
                >
                  <button
                    className="w-full flex items-center justify-between p-6 text-left focus-ring"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    data-testid={`faq-toggle-${index}`}
                  >
                    <span className="font-heading text-lg font-semibold text-[#2C4A3B] pr-4">{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-[#C07756] flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[#C07756] flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <p className="text-[#5A5A5A] leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-[#5A5A5A] mb-4">Still have questions?</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:6048610021" className="inline-flex items-center gap-2 text-[#2C4A3B] font-medium hover:text-[#C07756]">
                  <Phone className="w-5 h-5" /> Call Us: (604) 861-0021
                </a>
                <Button onClick={() => setShowPreApprovalPopup(true)} className="btn-primary">
                  Apply Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 bg-[#C07756]" data-testid="cta-section">
        <div className="container-custom text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Award className="w-4 h-4" />
            <span>98% Approval Rate</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Get Approved?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of BC families who found their perfect vehicle with YesApproved
          </p>
          <Button
            onClick={() => setShowPreApprovalPopup(true)}
            className="bg-[#2C4A3B] hover:bg-[#1e3529] text-white text-lg h-14 px-10 rounded-full"
            data-testid="cta-apply-btn"
          >
            Get Me Pre-Approved <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="text-white/70 text-sm mt-4">Takes only 60 seconds. No credit impact.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white py-16" data-testid="footer">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-6">
                <div className="w-10 h-10 bg-[#C07756] rounded-full flex items-center justify-center">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <span className="font-heading text-2xl font-bold">YesApproved</span>
              </div>
              <p className="text-white/60 mb-4">
                Powered by Cars 21 Motors<br />
                Surrey, British Columbia
              </p>
            </div>

            <div>
              <h4 className="font-heading text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li><a href="#why-us" className="text-white/60 hover:text-[#C07756] transition-colors">Why Choose Us</a></li>
                <li><a href="#tax-savings" className="text-white/60 hover:text-[#C07756] transition-colors">Tax Savings</a></li>
                <li><a href="#inventory" className="text-white/60 hover:text-[#C07756] transition-colors">Vehicle Inventory</a></li>
                <li><a href="#faq" className="text-white/60 hover:text-[#C07756] transition-colors">FAQ</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-heading text-lg font-semibold mb-4">Contact Us</h4>
              <ul className="space-y-3">
                <li>
                  <a href="tel:6048610021" className="flex items-center gap-2 text-white/60 hover:text-[#C07756] transition-colors">
                    <Phone className="w-4 h-4" /> (604) 861-0021
                  </a>
                </li>
                <li>
                  <a href="mailto:info@yesapproved.ca" className="flex items-center gap-2 text-white/60 hover:text-[#C07756] transition-colors">
                    <Mail className="w-4 h-4" /> info@yesapproved.ca
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-heading text-lg font-semibold mb-4">Legal</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-white/60 hover:text-[#C07756] transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="text-white/60 hover:text-[#C07756] transition-colors">Terms & Conditions</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-white/40 text-sm">
                © 2024 YesApproved.ca / Cars 21 Motors. All rights reserved.
              </p>
              <p className="text-white/60 text-sm text-center md:text-right max-w-md">
                We are committed to serving First Nations and Indigenous communities with respect, transparency, and fairness.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Apply Button */}
      <button
        onClick={() => setShowPreApprovalPopup(true)}
        className="fixed bottom-6 right-6 z-40 bg-[#C07756] hover:bg-[#A66548] text-white px-6 py-4 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center gap-2 animate-pulse-gentle"
        data-testid="floating-apply-btn"
      >
        <Car className="w-5 h-5" />
        <span className="font-medium">Get Pre-Approved</span>
      </button>

      {/* Live Chat Button */}
      <button
        onClick={() => setShowLiveChat(!showLiveChat)}
        className="fixed bottom-6 left-6 z-40 bg-[#2C4A3B] hover:bg-[#1e3529] text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all"
        data-testid="live-chat-btn"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Live Chat Widget */}
      {showLiveChat && (
        <div className="fixed bottom-24 left-6 z-50 w-80 bg-white rounded-2xl shadow-2xl border border-[#E5E0D6] overflow-hidden" data-testid="live-chat-widget">
          <div className="bg-[#2C4A3B] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageCircle className="w-5 h-5" />
              <span className="font-semibold">Ask About Your Approval</span>
            </div>
            <button onClick={() => setShowLiveChat(false)}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4">
            <p className="text-[#5A5A5A] text-sm mb-4">
              Hi! Have questions about your approval or tax savings? Our team is here to help.
            </p>
            <div className="space-y-2">
              <a href="tel:6048610021" className="flex items-center gap-2 text-[#2C4A3B] font-medium hover:text-[#C07756]">
                <Phone className="w-4 h-4" /> Call us: (604) 861-0021
              </a>
              <a href="mailto:info@yesapproved.ca" className="flex items-center gap-2 text-[#2C4A3B] font-medium hover:text-[#C07756]">
                <Mail className="w-4 h-4" /> Email us
              </a>
            </div>
            <Button onClick={() => { setShowPreApprovalPopup(true); setShowLiveChat(false); }} className="w-full btn-primary mt-4">
              Quick Pre-Approval
            </Button>
          </div>
        </div>
      )}

      {/* Pre-Approval Popup */}
      {showPreApprovalPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="pre-approval-popup">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full relative animate-fade-in max-h-[90vh] overflow-y-auto">
            <button 
              onClick={() => setShowPreApprovalPopup(false)}
              className="absolute top-4 right-4 text-[#5A5A5A] hover:text-[#2C4A3B]"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#2C4A3B] rounded-full flex items-center justify-center mx-auto mb-4">
                <Car className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-[#2C4A3B] mb-2">
                Get Pre-Approved Now!
              </h3>
              <p className="text-[#5A5A5A] text-sm">
                Quick 60-second application. No credit impact.
              </p>
            </div>

            <form onSubmit={handleQuickSubmit} className="space-y-4">
              <div>
                <Label htmlFor="quick_name" className="text-[#2C4A3B] font-medium">Full Name *</Label>
                <Input
                  id="quick_name"
                  name="full_name"
                  value={quickFormData.full_name}
                  onChange={handleQuickFormChange}
                  placeholder="John Doe"
                  className="input-custom mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="quick_phone" className="text-[#2C4A3B] font-medium">Phone Number *</Label>
                <Input
                  id="quick_phone"
                  name="phone"
                  type="tel"
                  value={quickFormData.phone}
                  onChange={handleQuickFormChange}
                  placeholder="(604) 861-0021"
                  className="input-custom mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="quick_email" className="text-[#2C4A3B] font-medium">Email *</Label>
                <Input
                  id="quick_email"
                  name="email"
                  type="email"
                  value={quickFormData.email}
                  onChange={handleQuickFormChange}
                  placeholder="john@example.com"
                  className="input-custom mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="quick_credit" className="text-[#2C4A3B] font-medium">Credit Situation</Label>
                <Select onValueChange={(value) => setQuickFormData(prev => ({ ...prev, credit_standing: value }))}>
                  <SelectTrigger className="input-custom mt-1">
                    <SelectValue placeholder="Select (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">Excellent</SelectItem>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                    <SelectItem value="bad">Bad Credit</SelectItem>
                    <SelectItem value="no_credit">No Credit</SelectItem>
                    <SelectItem value="bankruptcy">Bankruptcy</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="quick_first_nations"
                  checked={quickFormData.is_first_nations}
                  onCheckedChange={(checked) => setQuickFormData(prev => ({ ...prev, is_first_nations: checked }))}
                />
                <Label htmlFor="quick_first_nations" className="text-sm text-[#5A5A5A] cursor-pointer">
                  I'm a First Nations member (for tax savings info)
                </Label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="quick_consent"
                  checked={quickFormData.consent_contact}
                  onCheckedChange={(checked) => setQuickFormData(prev => ({ ...prev, consent_contact: checked }))}
                  required
                />
                <Label htmlFor="quick_consent" className="text-sm text-[#5A5A5A] cursor-pointer">
                  I consent to being contacted *
                </Label>
              </div>

              <Button type="submit" disabled={isSubmitting} className="w-full btn-primary">
                {isSubmitting ? 'Submitting...' : 'Get My Pre-Approval'} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>

              <p className="text-xs text-center text-[#5A5A5A]">
                <Shield className="inline w-3 h-3 mr-1" />
                Secure & encrypted. No credit impact.
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Tax Savings Info Popup */}
      {showTaxSavingsPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="tax-savings-popup">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-lg w-full relative animate-fade-in max-h-[90vh] overflow-y-auto">
            <button 
              onClick={() => setShowTaxSavingsPopup(false)}
              className="absolute top-4 right-4 text-[#5A5A5A] hover:text-[#2C4A3B]"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#C07756] rounded-full flex items-center justify-center mx-auto mb-4">
                <Percent className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-[#2C4A3B] mb-2">
                First Nations Tax Savings
              </h3>
              <p className="text-[#5A5A5A]">
                Save up to 12% on your vehicle purchase!
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-[#F0EBE0] p-4 rounded-xl">
                <h4 className="font-semibold text-[#2C4A3B] mb-2">PST Exemption (7%)</h4>
                <p className="text-sm text-[#5A5A5A]">BC Provincial Sales Tax exemption when vehicle is delivered to or sold on reserve land.</p>
              </div>
              <div className="bg-[#F0EBE0] p-4 rounded-xl">
                <h4 className="font-semibold text-[#2C4A3B] mb-2">GST Exemption (5%)</h4>
                <p className="text-sm text-[#5A5A5A]">Federal Goods & Services Tax exemption when property is bought on reserve or delivered there.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                <h4 className="font-semibold text-green-700 mb-2">Example: $50,000 Vehicle</h4>
                <p className="text-2xl font-bold text-green-600">Save $6,000!</p>
                <p className="text-sm text-green-600">That's $6,000 more for your family.</p>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-yellow-800">
                    <strong>Requirements:</strong> Valid Certificate of Indian Status card. Conditions apply based on delivery location. Our team will help you understand your eligibility!
                  </p>
                </div>
              </div>
            </div>

            <Button onClick={() => { setShowTaxSavingsPopup(false); setShowPreApprovalPopup(true); }} className="w-full btn-primary">
              Check My Eligibility <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      )}

      {/* Exit Intent Popup */}
      {showExitPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="exit-popup">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full relative animate-fade-in">
            <button 
              onClick={() => setShowExitPopup(false)}
              className="absolute top-4 right-4 text-[#5A5A5A] hover:text-[#2C4A3B]"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C07756] rounded-full flex items-center justify-center mx-auto mb-4">
                <Car className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-[#2C4A3B] mb-2">
                Wait! Don't Miss Out!
              </h3>
              <p className="text-[#5A5A5A] mb-4">
                Check if you qualify for auto financing in just 60 seconds. No credit impact!
              </p>
              <div className="bg-green-50 p-3 rounded-lg mb-4">
                <p className="text-sm text-green-700 font-medium">
                  🎁 First Nations? You could save up to $6,000 in taxes!
                </p>
              </div>
              <Button onClick={() => { setShowExitPopup(false); setShowPreApprovalPopup(true); }} className="w-full btn-primary mb-3">
                Get My Pre-Approval
              </Button>
              <button 
                onClick={() => setShowExitPopup(false)}
                className="text-[#5A5A5A] text-sm hover:text-[#C07756]"
              >
                No thanks, I'll pass
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
